import React, { useState, useCallback } from 'react';
import { ProductProps } from '../../models/product';
import {
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Typography,
  Box,
  Stack,
  Chip,
  Skeleton,
  useTheme,
} from '@mui/material';
import { SecondaryButton } from '../Button';

const ProductCard = (props: ProductProps) => {
  const theme = useTheme();
  const cardLineColor = theme.palette.grey[300];
  const {
    name,
    price,
    images,
    description,
    categories,
    setModalIsOpen,
    setModalData,
  } = props;
  const safeCategories = Array.isArray(categories) ? categories : [];
  const safeImages = Array.isArray(images) ? images : [];
  const mainImage = safeImages[0];
  const openModal = () => {
    setModalIsOpen(true);
    setModalData({
      name,
      price,
      images: safeImages,
      description,
      categories: safeCategories,
    });
  };
  const hasCategories = safeCategories.length > 0;
  const hasDescription = Boolean(description?.trim());
  const [imageLoaded, setImageLoaded] = useState(false);
  const handleImageLoad = useCallback(() => setImageLoaded(true), []);

  return (
    <Card
      data-testid={`${name}-product-card`}
      sx={{ my: 1, border: 1, borderRadius: '5%', borderColor: cardLineColor, boxShadow: 0 }}
    >
      <CardContent sx={{ pb: 1, '&:last-child': { pb: 1 } }}>
        <Box>
          <Typography variant="h5" align="center" sx={{ fontWeight: 700 }}>
            {name}
          </Typography>
        </Box>
        {hasCategories && (
          <Stack direction="row" flexWrap="wrap" justifyContent="center" spacing={0.5} useFlexGap sx={{ mt: 1, px: 0.5 }}>
            {safeCategories.map((cat, i) => (
              <Chip
                key={`${cat}-${i}`}
                label={cat}
                size="small"
                sx={{
                  bgcolor: 'grey.100',
                  color: 'text.secondary',
                  fontWeight: 500,
                  border: 'none',
                  '& .MuiChip-label': { px: 1.25 },
                }}
              />
            ))}
          </Stack>
        )}
        <Box sx={{ mt: hasCategories ? 1 : 0, position: 'relative' }}>
          {mainImage ? (
            <>
              {!imageLoaded && (
                <Skeleton
                  variant="rectangular"
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    borderRadius: '5%',
                    width: '100%',
                    height: '100%',
                  }}
                />
              )}
              <CardMedia
                component="img"
                alt={`${name}-main-image`}
                image={mainImage}
                data-testid="product-card-image"
                onLoad={handleImageLoad}
                sx={{
                  borderRadius: '5%',
                  width: '100%',
                  height: 'auto',
                  aspectRatio: '1 / 1',
                  objectFit: 'cover',
                  opacity: imageLoaded ? 1 : 0,
                  transition: 'opacity 0.2s ease-out',
                }}
              />
            </>
          ) : (
            <Box
              data-testid="product-card-image"
              sx={{
                borderRadius: '5%',
                width: '100%',
                aspectRatio: '1 / 1',
                bgcolor: 'grey.100',
              }}
              aria-hidden
            />
          )}
        </Box>
        {hasDescription && (
          <Typography
            variant="body2"
            align="center"
            color="text.secondary"
            sx={{
              mt: 1,
              px: 1,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {description}
          </Typography>
        )}
        <Box>
          <Typography variant="h6" align="center" sx={{ fontWeight: 700, pt: 1 }}>
            R{price}
          </Typography>
          <CardActions
            disableSpacing
            sx={{ justifyContent: 'center', px: 2, pt: 0.5, pb: 0.5 }}
          >
            <SecondaryButton
              onClick={openModal}
              width="min(100%, 280px)"
              style={{
                marginTop: 4,
                marginBottom: 0,
                marginLeft: 8,
                marginRight: 8,
                borderColor: cardLineColor,
              }}
            >
              View
            </SecondaryButton>
          </CardActions>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ProductCard;
